// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// SUPPLIER ↔ RETAILER — the one genuinely relational thing in the register.
//
// The "traders" field records who actually SOLD the product, and it is a real
// many-to-many: one supplier's fault surfaces across a dozen retail chains, and
// the same chain appears on recalls it did not originate. No table shows that,
// and the asymmetry it exposes — the company held responsible is rarely the
// shop you bought it from — is the point of the view.
//
// Two rules this view obeys, both learned the hard way elsewhere in the fleet:
//   * the layout SETTLES before first paint and is rendered exactly once; a
//     graph that visibly dances while converging is a defect, not an animation;
//   * clicking a node highlights its connections and dims everything else. A
//     click that only opens a panel wastes the graph entirely.

import { getBundle } from '../data';
import { esc, num } from '../format';
import { navigate } from '../router';
import { forceLayout, mulberry32, type SimLink, type SimNode } from '../utils/forceLayout';
import { attachSvgZoom } from '../utils/svgZoom';
import { chartFrame, labelChart, markTip, svgEl } from '../components/svg';

const W = 1000;
const H = 640;

interface Node extends SimNode { name: string; recalls: number; kind: 'supplier' | 'trader'; idx: number }

export function renderNetwork(host: HTMLElement): void {
  const { stats } = getBundle();
  const net = stats.network;
  const coveragePct = (100 * net.coverage.linked) / net.coverage.total;

  host.innerHTML = `
    <div class="view-head">
      <h2>Who made it, and who sold it</h2>
      <p>Recall notices name a ${'supplier'} — the company responsible — and separately list the retailers who
      stocked the product. Joining those two gives a network the register never draws: the
      ${num(net.suppliers.length)} most-connected suppliers against the ${num(net.traders.length)} retailers
      that appear most often. Click any node to trace its connections.</p>
    </div>
    <div id="graph"></div>
    <div class="legend">
      <span class="legend-item"><span class="legend-swatch" style="background:#0f5c63"></span>Supplier (responsible for the recall)</span>
      <span class="legend-item"><span class="legend-swatch" style="background:#c2410c"></span>Retailer / marketplace (where it was sold)</span>
      <span class="legend-item muted">Node size is how many recalls that company appears on. Scroll to zoom, drag to pan.
      Only the larger nodes are keyboard-focusable; every company is reachable by name from Search.</span>
    </div>
    <div class="note caveat">
      The retailer field is free prose — “Sold on TEMU”, “Kmart, Target and Big W” — so it has to be split and
      cleaned rather than read. ${num(net.coverage.linked)} of ${num(net.coverage.total)} recalls
      (${coveragePct.toFixed(0)}%) yielded at least one usable retailer name; the rest name no retailer, or name
      one in a form too free-form to parse. Retailers appearing fewer than five times are left out to keep the
      graph legible, so this is the shape of the well-recorded part of the register, not all of it.
    </div>
  `;

  host.querySelector<HTMLElement>('#graph')!.append(graph(net));
}

function graph(net: ReturnType<typeof getBundle>['stats']['network']): HTMLElement {
  const { figure, mount } = chartFrame({
    title: 'Suppliers and the retailers that sold their recalled products',
    subtitle: 'A link means at least one recall where that supplier’s product was sold by that retailer.',
    // Scrolls locally like every other wide chart here. Allowed to shrink to
    // 375px the graph rendered at 0.375 scale, which put every node under 6 CSS
    // px — too small to hit with a thumb and too small to read.
    scroll: true,
    minWidth: 760,
  });

  const nodes: Node[] = [];
  const rnd = mulberry32(20260807);

  // Seeded in two vertical bands so the bipartite structure is legible from the
  // first frame and the layout converges fast — a uniform random cloud both
  // takes longer and settles into a shape that hides the two sides.
  net.suppliers.forEach((s, i) => {
    nodes.push({
      name: s.name, recalls: s.recalls, kind: 'supplier', idx: i,
      x: W * 0.28 + (rnd() - 0.5) * W * 0.22,
      y: H * (0.08 + 0.84 * (i / Math.max(1, net.suppliers.length - 1))) + (rnd() - 0.5) * 24,
      r: radius(s.recalls),
    });
  });
  const offset = net.suppliers.length;
  net.traders.forEach((t, i) => {
    nodes.push({
      name: t.name, recalls: t.recalls, kind: 'trader', idx: i,
      x: W * 0.72 + (rnd() - 0.5) * W * 0.22,
      y: H * (0.08 + 0.84 * (i / Math.max(1, net.traders.length - 1))) + (rnd() - 0.5) * 24,
      r: radius(t.recalls),
    });
  });

  const links: SimLink[] = net.links.map((l) => ({ source: l.s, target: offset + l.t }));

  // Runs to completion synchronously. Nothing is added to the DOM until this
  // returns, so the graph is motionless from the very first painted frame.
  forceLayout(nodes, links, { width: W, height: H, iterations: 320 });

  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, width: W, height: H, preserveAspectRatio: 'xMidYMid meet' });
  svg.style.minWidth = '760px';
  labelChart(svg, 'Network of suppliers and the retailers that sold their recalled products',
    `${net.suppliers.length} suppliers and ${net.traders.length} retailers connected by ${net.links.length} supplier-retailer pairs.`);

  const linkLayer = svgEl('g', { stroke: 'var(--border-default)', 'stroke-opacity': '0.5', fill: 'none' });
  const nodeLayer = svgEl('g');
  const labelLayer = svgEl('g', { 'pointer-events': 'none' });
  svg.append(linkLayer, nodeLayer, labelLayer);

  const linkEls: SVGLineElement[] = [];
  net.links.forEach((l, i) => {
    const a = nodes[l.s];
    const b = nodes[offset + l.t];
    const line = svgEl('line', {
      x1: a.x.toFixed(1), y1: a.y.toFixed(1), x2: b.x.toFixed(1), y2: b.y.toFixed(1),
      'stroke-width': Math.min(3, 0.4 + l.w * 0.25).toFixed(2),
    });
    line.dataset.s = String(l.s);
    line.dataset.t = String(offset + l.t);
    line.dataset.i = String(i);
    linkLayer.append(line);
    linkEls.push(line);
  });

  const nodeEls: SVGGElement[] = [];
  let selected: number | null = null;

  const clearSelection = () => {
    selected = null;
    for (const el of nodeEls) el.classList.remove('dim', 'sel');
    for (const el of linkEls) { el.classList.remove('dim'); el.setAttribute('stroke-opacity', '0.5'); }
  };

  const select = (i: number) => {
    if (selected === i) { clearSelection(); return; }
    selected = i;
    const connected = new Set<number>([i]);
    for (const l of net.links) {
      if (l.s === i) connected.add(offset + l.t);
      if (offset + l.t === i) connected.add(l.s);
    }
    nodeEls.forEach((el, k) => {
      el.classList.toggle('dim', !connected.has(k));
      el.classList.toggle('sel', k === i);
    });
    linkEls.forEach((el) => {
      const on = Number(el.dataset.s) === i || Number(el.dataset.t) === i;
      el.classList.toggle('dim', !on);
      el.setAttribute('stroke-opacity', on ? '0.95' : '0.08');
    });
  };

  /*
   * Only the HUBS are keyboard-focusable.
   *
   * Making all 225 nodes tab stops meant a keyboard user had to press Tab 225
   * times to cross this view, and most of those stops were 7px circles that a
   * pointer cannot reliably hit either. Restricting focus to nodes big enough to
   * be a real target keeps the view traversable; every company in the graph,
   * hub or not, is still reachable by name from Search and from the Repeat
   * suppliers table, and every node still shows its figures on hover.
   */
  const FOCUSABLE_R = 7;
  nodes.forEach((n, i) => {
    const focusable = n.r >= FOCUSABLE_R;
    const g = focusable
      ? svgEl('g', { class: 'mark', role: 'button', tabindex: '0', style: 'cursor:pointer' })
      : svgEl('g', { class: 'mark', style: 'cursor:pointer' });
    const circle = svgEl('circle', {
      cx: n.x.toFixed(1), cy: n.y.toFixed(1), r: n.r.toFixed(1),
      fill: n.kind === 'supplier' ? '#0f5c63' : '#c2410c',
      'fill-opacity': 0.85, stroke: 'var(--bg-surface)', 'stroke-width': 1.2,
    });
    g.append(circle);
    markTip(g,
      `${n.name}\n${n.kind === 'supplier' ? 'Supplier' : 'Retailer'}\nAppears on ${num(n.recalls)} recall${n.recalls === 1 ? '' : 's'}\nClick to trace connections`,
      `${n.name}, ${n.kind}, ${num(n.recalls)} recalls. Select to trace connections.`);

    g.addEventListener('click', () => select(i));
    g.addEventListener('dblclick', () => navigate('search', { q: n.name }));
    g.addEventListener('keydown', (e) => {
      const ev = e as KeyboardEvent;
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); select(i); }
    });
    nodeLayer.append(g);
    nodeEls.push(g as SVGGElement);

  });

  /*
   * Labels: hubs first, and a label is DROPPED rather than drawn over one that
   * is already placed.
   *
   * Labelling every node above a size threshold produced exactly the smear this
   * was meant to avoid — "Kmart", "Myer", "Toys R Us" and "Harvey Norman" all
   * printed across each other in the dense middle, which is unreadable and
   * looks like a rendering fault. Placing them in descending size order and
   * skipping any that would collide means the biggest hubs always win, and the
   * rest are named on hover and on selection where there is room to read them.
   */
  const placed: { x1: number; y1: number; x2: number; y2: number }[] = [];
  const CHAR_W = 5.9;
  const LINE_H = 13;
  const labelled = nodes
    .map((n, i) => ({ n, i }))
    .filter(({ n }) => n.r >= 7)
    .sort((a, b) => b.n.recalls - a.n.recalls);

  for (const { n } of labelled) {
    const text = n.name.length > 22 ? `${n.name.slice(0, 21)}…` : n.name;
    const x1 = n.x + n.r + 4;
    const y1 = n.y - LINE_H / 2;
    const box = { x1, y1, x2: x1 + text.length * CHAR_W, y2: y1 + LINE_H };
    if (box.x2 > W - 2) continue;                       // would run off the canvas
    if (placed.some((p) => !(box.x2 < p.x1 || box.x1 > p.x2 || box.y2 < p.y1 || box.y1 > p.y2))) continue;
    placed.push(box);
    const t = svgEl('text', {
      x: x1.toFixed(1), y: (n.y + 4).toFixed(1),
      'font-size': 11, fill: 'var(--text-primary)',
      stroke: 'var(--bg-base)', 'stroke-width': 3, 'paint-order': 'stroke',
    });
    t.textContent = text;
    labelLayer.append(t);
  }

  // Clicking empty canvas clears the selection — a selection you cannot undo is
  // its own small trap.
  svg.addEventListener('click', (e) => {
    if ((e.target as Element).closest('g.mark')) return;
    clearSelection();
  });

  mount.append(svg);
  // attachSvgZoom must run AFTER the svg is in the DOM: it looks up
  // svg.parentElement to mount its controls, and mounting it earlier silently
  // renders no controls at all.
  attachSvgZoom(svg);
  void esc;
  return figure;
}

function radius(recalls: number): number {
  return Math.max(3.5, Math.min(22, 3 + Math.sqrt(recalls) * 1.6));
}
